import { useLocation } from '@gatsbyjs/reach-router'
import * as R from 'ramda'
import { useCallback } from 'react'

import { isNotNull } from '../../../utils/collectionTools'
import { mergeUndefinedRight } from '../../../utils/ramda'
import { addFragmentToUrl, addQueryParamsToUrl, createQueryParam } from '../../../utils/url'
import { callbackSlugs } from '../../auth/config'
import { addDispensaryBasePathToPath, useDispensaryContext } from '../../dispensaries'
import { accountsReturnToParam } from '../config'
import { AccountsPageType, RegisterType } from '../types'
import { getAccountsUrlForPage } from '../utils'

const classicSiteURL = process.env.GATSBY_CLASSIC_SITE_URL

export interface UseAccountsUrl {
  getRedirectUrl: (options: AccountsRedirectOptions) => string
  redirectToAccount: (options: AccountsRedirectOptions) => void
}

export type AccountsRedirectOptions =
  | {
      page?: Exclude<AccountsPageType, 'register'>
    }
  | AccountsRedirectOptionsForRegisterPage

type AccountsRedirectOptionsForRegisterPage = {
  page: Extract<AccountsPageType, 'register'>
  registerType?: RegisterType | null
}

const defaultOptions: AccountsRedirectOptions = {
  page: 'base',
}

const callbackUrlForReturnTo = R.pipe(
  (returnTo: string) => [createQueryParam(accountsReturnToParam, returnTo)].filter(isNotNull),
  //temporary redirection to the classic site when the registration is disabled
  addQueryParamsToUrl(classicSiteURL ? `${classicSiteURL}${callbackSlugs.login}` : ''),
)

const isOptionsForRegisterPage = (
  options: AccountsRedirectOptions,
): options is AccountsRedirectOptionsForRegisterPage => options?.page === 'register'

const isOptionsForCheckoutPage = (
  options: AccountsRedirectOptions,
): options is { page: 'checkout' } => options?.page === 'checkout'

const addRegisterTypeFragment =
  (options: AccountsRedirectOptions) =>
  (url: string): string =>
    isOptionsForRegisterPage(options) && options.registerType
      ? addFragmentToUrl(url, options.registerType)
      : url

export const useAccountsUrl = (): UseAccountsUrl => {
  const location = useLocation()
  const dispensary = useDispensaryContext()

  const practitionerCode = dispensary ? dispensary.practitionerCode : null

  const getCurrentPath = useCallback(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location],
  )

  const getOnboardingPath = useCallback(
    () =>
      dispensary ? addDispensaryBasePathToPath(dispensary.slug, '/onboarding') : '/onboarding',
    [dispensary],
  )

  const getCheckoutPath = useCallback(
    () =>
      dispensary
        ? addDispensaryBasePathToPath(dispensary.slug, '/checkout/shipping')
        : '/checkout/shipping',
    [dispensary],
  )

  const getReturnPath = useCallback(
    (options: AccountsRedirectOptions): string => {
      if (isOptionsForRegisterPage(options)) {
        return getOnboardingPath()
      }

      if (isOptionsForCheckoutPage(options)) {
        return getCheckoutPath()
      }
      return getCurrentPath()
    },
    [getCurrentPath, getOnboardingPath, getCheckoutPath],
  )

  const getRedirectUrl = useCallback(
    (options: AccountsRedirectOptions = defaultOptions): string => {
      const redirectOptions = mergeUndefinedRight(defaultOptions, options)
      const accountsUrl = getAccountsUrlForPage(redirectOptions.page)
      const params = [
        createQueryParam('practitionerCode', practitionerCode),
        createQueryParam('callbackUrl', callbackUrlForReturnTo(getReturnPath(redirectOptions))),
      ]
      return R.pipe(
        R.filter<string>(isNotNull),
        addQueryParamsToUrl(accountsUrl),
        addRegisterTypeFragment(redirectOptions),
      )(params)
    },
    [practitionerCode, getReturnPath],
  )

  const redirectToAccount = useCallback(
    (options: AccountsRedirectOptions = defaultOptions): void => {
      if (typeof window === 'undefined') {
        return
      }
      window.location.href = getRedirectUrl(options)
    },
    [getRedirectUrl],
  )

  return {
    getRedirectUrl,
    redirectToAccount,
  }
}
